import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Link as LinkIcon,
} from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";
import api from "../../../services/api";
import NewTicketModal from "../../../components/NewTicketModal";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  chipPrincipal: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },
  chipDepartamento: {
    margin: theme.spacing(0.5),
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
}));

const ClienteContatos = ({ clienteId, fieldRequirements = {} }) => {
  const classes = useStyles();
  const history = useHistory();
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [selectedWhatsappId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [contactSearchParam, setContactSearchParam] = useState("");
  const [contactOptions, setContactOptions] = useState([]);
  const [selectedContactOption, setSelectedContactOption] = useState(null);
  const [loadingContactOptions, setLoadingContactOptions] = useState(false);
  const [contatoEdit, setContatoEdit] = useState(null);
  const [savingContato, setSavingContato] = useState(false);
  const submitLockRef = useRef(false);
  const [formData, setFormData] = useState({
    nome: "",
    cargo: "",
    telefone: "",
    ddd_telefone: "",
    celular: "",
    ddd_celular: "",
    email: "",
    email_secundario: "",
    whatsapp: "",
    linkedin: "",
    observacoes: "",
    principal: false,
    ativo: true,
  });

  useEffect(() => {
    if (clienteId) {
      loadContatos();
    }
  }, [clienteId]);

  useEffect(() => {
    if (!linkModalOpen) {
      return;
    }

    const debounce = setTimeout(async () => {
      setLoadingContactOptions(true);
      try {
        const { data } = await api.get("/contacts/list", {
          params: {
            searchParam: contactSearchParam,
            limit: 30
          }
        });
        setContactOptions(data || []);
      } catch (error) {
        console.error("Erro ao carregar contatos para vínculo:", error);
        toast.error("Erro ao carregar contatos");
      } finally {
        setLoadingContactOptions(false);
      }
    }, 350);

    return () => clearTimeout(debounce);
  }, [linkModalOpen, contactSearchParam]);

  // Função para formatar telefone dinamicamente
  const formatPhone = (value) => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "");
    
    if (digits.length <= 4) {
      return digits;
    } else if (digits.length <= 8) {
      return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    } else {
      return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
    }
  };

  const handlePhoneMaskChange = (field) => (event) => {
    const value = event.target.value;
    const digits = value.replace(/\D/g, "").slice(0, 9); // Limita a 9 dígitos
    
    // Salva apenas os dígitos
    setFormData({ ...formData, [field]: digits });
  };

  const loadContatos = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clientes/${clienteId}/contatos`);
      setContatos(data);
    } catch (error) {
      console.error("Erro ao carregar contatos:", error);
      toast.error("Erro ao carregar contatos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (contato = null) => {
    setSavingContato(false);
    submitLockRef.current = false;

    if (contato) {
      setContatoEdit(contato);
      setFormData(contato);
    } else {
      setContatoEdit(null);
      setFormData({
        nome: "",
        cargo: "",
        telefone: "",
        ddd_telefone: "",
        celular: "",
        ddd_celular: "",
        email: "",
        email_secundario: "",
        whatsapp: "",
        linkedin: "",
        observacoes: "",
        principal: false,
        ativo: true,
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setContatoEdit(null);
    setSavingContato(false);
    submitLockRef.current = false;
  };

  const handleOpenLinkModal = () => {
    setSelectedContactOption(null);
    setContactSearchParam("");
    setContactOptions([]);
    setLinkModalOpen(true);
  };

  const handleCloseLinkModal = () => {
    setLinkModalOpen(false);
    setSelectedContactOption(null);
    setContactSearchParam("");
    setContactOptions([]);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === "checkbox"
      ? event.target.checked
      : event.target.value;
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async () => {
    if (submitLockRef.current || savingContato) {
      return;
    }

    submitLockRef.current = true;
    setSavingContato(true);

    try {
      const fieldLabels = {
        nome: "Nome do contato",
        email: "E-mail",
        celular: "Celular",
        cargo: "Cargo"
      };

      const requiredFields = Object.keys(fieldRequirements || {}).filter(
        (fieldName) => Boolean(fieldRequirements[fieldName])
      );

      const missingFields = requiredFields.filter((fieldName) => {
        // O campo unico "Celular / WhatsApp" (formData.whatsapp) satisfaz a
        // obrigatoriedade de celular
        const value =
          fieldName === "celular"
            ? formData.celular || formData.whatsapp
            : formData[fieldName];
        return value === null || value === undefined || String(value).trim() === "";
      });

      if (missingFields.length > 0) {
        const missingLabels = missingFields.map(
          (fieldName) => fieldLabels[fieldName] || fieldName
        );

        toast.error(`Preencha os campos obrigatórios: ${missingLabels.join(", ")}`);
        return;
      }

      if (contatoEdit) {
        await api.put(
          `/clientes/${clienteId}/contatos/${contatoEdit.id}`,
          formData
        );
        toast.success("Contato atualizado com sucesso!");
      } else {
        await api.post(`/clientes/${clienteId}/contatos`, formData);
        toast.success("Contato cadastrado com sucesso!");
      }

      handleCloseModal();
      await loadContatos();
    } catch (error) {
      console.error("Erro ao salvar contato:", error);
      const errorMessage = error.response?.data?.error || "Erro ao salvar contato";
      toast.error(errorMessage);
    } finally {
      submitLockRef.current = false;
      setSavingContato(false);
    }
  };

  const handleLinkExistingContact = async () => {
    if (!selectedContactOption?.id) {
      toast.warning("Selecione um contato para vincular");
      return;
    }

    try {
      const { data } = await api.post(`/contacts/${selectedContactOption.id}/link-cliente`, {
        clienteId
      });

      if (data?.alreadyLinked) {
        toast.info("Contato já estava vinculado a este cliente");
      } else {
        toast.success("Contato vinculado com sucesso!");
      }

      handleCloseLinkModal();
      loadContatos();
    } catch (error) {
      console.error("Erro ao vincular contato:", error);
      const errorMessage = error.response?.data?.error || "Erro ao vincular contato";
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente desvincular este contato do cliente? A pessoa continua existindo nos contatos gerais.")) {
      return;
    }

    try {
      await api.delete(`/clientes/${clienteId}/contatos/${id}`);
      toast.success("Contato desvinculado do cliente!");
      loadContatos();
    } catch (error) {
      console.error("Erro ao desvincular contato:", error);
      toast.error("Erro ao desvincular contato");
    }
  };

  const buildContactNumber = (contato) => {
    const raw = (contato?.whatsapp || "").trim();
    if (raw) {
      return raw;
    }

    const ddd = String(contato?.ddd_celular || "").replace(/\D/g, "");
    const celular = String(contato?.celular || "").replace(/\D/g, "");
    if (!ddd || !celular) {
      return "";
    }

    return `${ddd}${celular}`;
  };

  const resolveOrCreateContact = async (contato) => {
    if (contato?.contactId) {
      return {
        id: Number(contato.contactId),
        name: contato?.nome || "Contato sem nome",
        number: buildContactNumber(contato),
        email: contato?.email || ""
      };
    }

    const numero = buildContactNumber(contato);
    if (!numero) {
      return null;
    }

    try {
      const { data } = await api.get(`/contacts/bynumber/${encodeURIComponent(numero)}`);
      if (data?.contact?.id) {
        return data.contact;
      }
    } catch (error) {
      // Ignora lookup e tenta criar contato abaixo.
    }

    const { data: createdContact } = await api.post("/contacts", {
      name: contato?.nome || "Contato sem nome",
      number: numero,
      email: contato?.email || ""
    });

    return createdContact?.id ? createdContact : null;
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket?.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleWhatsApp = async (contato) => {
    try {
      // Autor: GitHub Copilot | Data: 20/04/2026 | Motivo: usar o mesmo fluxo do /contacts, com escolha de fila e conexao no NewTicketModal.
      const resolvedContact = await resolveOrCreateContact(contato);
      if (!resolvedContact?.id) {
        toast.error("Não foi possível identificar o contato para abrir conversa");
        return;
      }

      setContactTicket(resolvedContact);
      setNewTicketModalOpen(true);
    } catch (error) {
      console.error("Erro ao abrir atendimento do contato:", error);
      const errorMessage = error.response?.data?.error || "Erro ao abrir atendimento";
      toast.error(errorMessage);
    }
  };

  const formatPhoneDisplay = (ddd, phone) => {
    if (!phone) return "-";
    return ddd ? `(${ddd}) ${phone}` : phone;
  };

  return (
    <Box>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        initialWhatsappId={selectedWhatsappId}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />

      <Box className={classes.header}>
        <Typography variant="h6">
          <PersonIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
          Contatos Cadastrados
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            style={{ marginRight: 8 }}
          >
            Novo Contato
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<LinkIcon />}
            onClick={handleOpenLinkModal}
          >
            Vincular Contato Existente
          </Button>
        </Box>
      </Box>

      <Paper>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Cargo</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>Celular</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contatos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum contato cadastrado
                </TableCell>
              </TableRow>
            ) : (
              contatos.map((contato) => (
                <TableRow key={contato.id}>
                  <TableCell>
                    <strong>{contato.nome}</strong>
                  </TableCell>
                  <TableCell>
                    {contato.cargo && <div>{contato.cargo}</div>}
                  </TableCell>
                  <TableCell>
                    {formatPhoneDisplay(contato.ddd_telefone, contato.telefone)}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {formatPhoneDisplay(contato.ddd_celular, contato.celular)}
                      {contato.whatsapp && (
                        <IconButton
                          size="small"
                          onClick={() => handleWhatsApp(contato)}
                          style={{ color: "#25D366" }}
                        >
                          <WhatsAppIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {contato.email && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <EmailIcon fontSize="small" />
                        {contato.email}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {contato.principal && (
                      <Chip
                        label="Principal"
                        size="small"
                        className={classes.chipPrincipal}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenModal(contato)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(contato.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Modal de Cadastro/Edição */}
      <Dialog
        open={modalOpen}
        onClose={() => {
          if (savingContato) {
            return;
          }
          handleCloseModal();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {contatoEdit ? "Editar Contato" : "Novo Contato"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} md={8}>
              <TextField
                label="Nome do Contato"
                fullWidth
                value={formData.nome}
                onChange={handleChange("nome")}
                required={Boolean(fieldRequirements.nome)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.principal}
                    onChange={handleChange("principal")}
                    color="primary"
                  />
                }
                label="Contato Principal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Cargo"
                fullWidth
                value={formData.cargo}
                onChange={handleChange("cargo")}
                required={Boolean(fieldRequirements.cargo)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="E-mail"
                fullWidth
                type="email"
                value={formData.email}
                onChange={handleChange("email")}
                required={Boolean(fieldRequirements.email)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="E-mail Secundário"
                fullWidth
                type="email"
                value={formData.email_secundario}
                onChange={handleChange("email_secundario")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="LinkedIn"
                fullWidth
                value={formData.linkedin}
                onChange={handleChange("linkedin")}
                placeholder="https://linkedin.com/in/..."
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                label="DDD"
                fullWidth
                value={formData.ddd_telefone}
                onChange={handleChange("ddd_telefone")}
                inputProps={{ maxLength: 3 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Telefone"
                fullWidth
                value={formatPhone(formData.telefone)}
                onChange={handlePhoneMaskChange("telefone")}
                placeholder="9999-9999 ou 99999-9999"
                inputProps={{ maxLength: 10 }} // 9 dígitos + 1 hífen
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Celular / WhatsApp"
                fullWidth
                value={formData.whatsapp}
                onChange={handleChange("whatsapp")}
                placeholder="5542999999999"
                helperText="Formato: código do país + DDD + número"
                required={Boolean(fieldRequirements.celular)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Observações"
                fullWidth
                multiline
                rows={3}
                value={formData.observacoes}
                onChange={handleChange("observacoes")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={savingContato}>Cancelar</Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={savingContato}
          >
            {savingContato ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={linkModalOpen}
        onClose={handleCloseLinkModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Vincular contato existente</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={contactOptions}
            loading={loadingContactOptions}
            value={selectedContactOption}
            onChange={(event, value) => setSelectedContactOption(value)}
            getOptionLabel={(option) => {
              const number = option?.number ? ` (${option.number})` : "";
              return `${option?.name || ""}${number}`;
            }}
            getOptionSelected={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar contato"
                variant="outlined"
                margin="dense"
                onChange={(event) => setContactSearchParam(event.target.value || "")}
                helperText="Digite nome, número, e-mail ou LID"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkModal}>Cancelar</Button>
          <Button
            onClick={handleLinkExistingContact}
            color="primary"
            variant="contained"
          >
            Vincular
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClienteContatos;
